/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { getBreakpointMediaQuery } from 'js/constants.js';
import { scrollToAnchor } from 'js/ra.js';
import '../../../../../common/filters/date-picker.js';
import { DateTime } from 'luxon';
import { hasDatePassed, formatIsoDateTime } from 'js/helpers.js';
import { createFilledElement } from '../../../../../common/filters/fill-template.js';
import * as FS from '../../../../../common/filters/filter-shared.js';
import * as pagination from '../../../../../common/filters/pagination.js';

const componentName = 'generic-filter';

class GenericFilter extends HTMLElement {

    // ==========
    // COMPONENT SETUP
    // ==========
    elements = {
        // Config
        data: this.querySelector(`.${componentName}__data`),
        // Filter
        filterDialog: this.querySelector(FS.selectors.dialog),
        filterMobileShow: this.querySelector(FS.selectors.mobileShow),
        filterMobileShowPlex: this.querySelector(FS.selectors.mobileShowPlex),
        filterMobileClose: this.querySelector(FS.selectors.mobileClose),
        filterMobileSubmit: this.querySelector(FS.selectors.mobileSubmit),
        filterMobileSubmitPlex: this.querySelector(FS.selectors.mobileSubmitPlex),
        filterForm: this.querySelector(FS.selectors.form),
        filterResets: this.querySelectorAll(FS.selectors.resets),
        filterDetails: this.querySelectorAll(FS.selectors.details),
        facetSearches: this.querySelectorAll(FS.selectors.facetSearches),
        checkboxes: this.querySelectorAll(FS.selectors.checkboxes),
        // Filter chips
        filterMobileHeaderBottom: this.querySelector(FS.selectors.mobileHeaderBottom),
        filterChipsContainers: this.querySelectorAll(FS.selectors.chipsContainers),
        filterChipTemplate: this.querySelector(FS.selectors.chipTemplate),
        // Results
        desktopSortSelect: this.querySelector(FS.selectors.desktopSortSelect),
        resultsContentContainer: this.querySelector(FS.selectors.resultsContentContainer),
        resultTemplate: this.querySelector(`.${componentName}__result-template`),
        // Pagination
        paginationForm: this.querySelector(FS.selectors.paginationForm),
    };

    constants = Object.freeze({
        listingService: this.elements.data.dataset.listingService,
        previewTokenName: this.elements.data.dataset.previewTokenName,
        previewTokenValue: this.elements.data.dataset.previewTokenValue,
        theme: this.elements.data.dataset.theme,
        sortProp: 'sort',
        numPerPageProp: 'numPerPage',
        pageProp: 'page',
    });

    state = {
        // [LEGACY BREAKPOINT: max-width 1024px -> getBreakpointMediaQuery('down', 'medium')]
        matchMobile: getBreakpointMediaQuery('down', 'medium'),
        currentPage: 1,
        numPerPage: () => Number(new FormData(this.elements.filterForm).get(this.constants.numPerPageProp)),
    };

    /**
     * Partial type definition of a result object
     * @typedef {{
     *     title: string?,
     *     description: string?,
     *     link: string?,
     *     image: string?,
     *     contentType: string?,
     *     date: string?,
     * }} ResultItem
     */

    // ==========
    // LOAD CONTENT
    // ==========
    runQuery = async (initialLoad = false) => {
        window.dispatchEvent(new CustomEvent('show-loading'));
        this.updateFilterUi();

        // Perform query request
        const searchParams = this.getSearchParams();
        let url = `${this.constants.listingService}?${searchParams.toString()}`;
        if (this.constants.previewTokenName && this.constants.previewTokenValue) {
            // Manually append this query parameter token to avoid URL encoding
            url += `${searchParams.toString().trim() ? '&' : ''}${this.constants.previewTokenName}=${this.constants.previewTokenValue}`;
        }
        const response = await fetch(url);

        /**
         * @type {{
         *     facetCounts: Record<string, Record<string, number>>
         *     loadMore: {
         *         currentPage: number,
         *         totalMatches: number,
         *         totalPages: number,
         *     },
         *     message: string,
         *     next: string,
         *     query: string,
         *     results: ResultItem[],
         *     success: boolean,
         * }}
         */
        const json = await response.json();

        // Display results
        const resultElements = json.results.map((result) => this.createResultElement(result));
        this.elements.resultsContentContainer.replaceChildren(...resultElements);

        // update UI result counts
        FS.updateResultCounts(this, this.elements.paginationForm, {
            firstResultNum: (json.loadMore.currentPage - 1) * this.state.numPerPage() + 1,
            lastResultNum: Math.min(json.loadMore.currentPage * this.state.numPerPage(), json.loadMore.totalMatches),
            totalResults: json.loadMore.totalMatches,
            currentPageNum: json.loadMore.currentPage,
            pageCount: json.loadMore.totalPages,
        });

        // update facet counts
        const tagCountArray = Object.values(json.facetCounts).flatMap((facet) => Object.entries(facet));
        const tagCountObject = Object.fromEntries(tagCountArray);

        [...this.elements.checkboxes].forEach((checkbox) => {
            const isWebinarType = checkbox.name === 'webinarTypeFacet' || checkbox.value.includes('rockwell-automation:metadata/webinar-type') || checkbox.value.includes('plex:metadata/webinar-type');
            FS.updateFacetItemCount(checkbox, tagCountObject[checkbox.value] ?? 0, isWebinarType);
        });

        // hide facets with zero results
        FS.hideFacetsWithNoResults(this.elements.filterDetails);

        // determine which filter facets should display search fields
        if (initialLoad) {
            FS.showFilterSearchFields(this.elements.filterDetails);

            // Overrides Webinar Type Facets
            const liveWebinarInput = document.querySelector('input[value="rockwell-automation:metadata/webinar-type/live-webinar"], input[value="plex:metadata/webinar-type/live-webinar"]');
            liveWebinarInput?.setAttribute('value', 'live');
            liveWebinarInput?.setAttribute('name', 'webinarTypeFacet');
            const onDemandWebinarInput = document.querySelector('input[value="rockwell-automation:metadata/webinar-type/on-demand-webinar"], input[value="plex:metadata/webinar-type/on-demand-webinar"]');
            onDemandWebinarInput?.setAttribute('value', 'on-demand');
            onDemandWebinarInput?.setAttribute('name', 'webinarTypeFacet');
        }

        this.hidden = false;
        if (!initialLoad) {
            scrollToAnchor(this);
        }

        window.dispatchEvent(new CustomEvent('hide-loading'));
    };

    /** @returns {URLSearchParams} */
    getSearchParams = () => {
        let formData = [...new FormData(this.elements.filterForm)]
            .filter(([, value]) => value !== '');

        // Overrides webinar type behavior
        const webinarTypeCount = formData.reduce((count, item) => (item[0] === 'webinarTypeFacet' ? count + 1 : count), 0);

        // Remove "webinarTypeFacet" if it appears twice (selected as both live and on-demand)
        formData = webinarTypeCount > 1
            ? formData.filter((item) => item[0] !== 'webinarTypeFacet')
            : formData;

        // Exclude past events if live webinar is selected
        if (formData.some((item) => item[0] === 'webinarTypeFacet' && item[1] === 'live')) {
            formData.push(['upcomingEventsFlag', 'true']);
        }

        // Exclude future events if on-demand webinar is selected
        if (formData.some((item) => item[0] === 'webinarTypeFacet' && item[1] === 'on-demand')) {
            formData.push(['pastEventsFlag', 'true']);
        }

        const searchParams = new URLSearchParams(formData);
        searchParams.set(this.constants.pageProp, this.state.currentPage);
        return searchParams;
    };

    /**
     * Get pre-selected tags from query parameters and apply the filters on the page.
     * This function also supports comma separated multiple selections.
     * @example
     * `?capability=consulting-and-integration-services,digital-transformation`
     */
    readUrlParams = () => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        urlSearchParams.forEach((value, key) => {
            value.split(',').forEach((splitValue) => {
                let tagId = '';
                switch (this.constants.theme) {
                case 'px':
                    tagId = `plex:metadata/${key}/${splitValue}`;
                    break;
                default:
                    tagId = `rockwell-automation:metadata/${key}/${splitValue}`;
                }
                FS.fillInput([...this.elements.filterForm.elements], key, tagId, true);
            });
        });
    };

    updateFilterUi = () => {
        // Identify selected filter values
        const hasChips = FS.updateChips(
            this.elements.filterForm,
            this.elements.filterChipTemplate,
            this.handleFilterForm,
            this.elements.filterChipsContainers,
            [],
            this,
        );

        // Hide/unhide mobile header bottom and reset buttons
        this.elements.filterMobileHeaderBottom.hidden = !hasChips;
        this.elements.filterResets.forEach((el) => {
            el.disabled = !hasChips;
        });

        // Sync desktop sort select (the mobile sort radio buttons are used as source of truth)
        this.elements.desktopSortSelect.value = new FormData(this.elements.filterForm).get(this.constants.sortProp);
        this.elements.desktopSortSelect.sync?.();
    };

    formatDate = (isEventFormat, startDate, endDate, pageLocale) => {
        if (!startDate) return '';

        const luxonStartDate = DateTime.fromISO(startDate, { setZone: isEventFormat });
        const startDateLocaleString = luxonStartDate.setLocale(pageLocale).toLocaleString(DateTime.DATE_FULL);

        if (!endDate) return startDateLocaleString;

        const luxonEndDate = DateTime.fromISO(endDate, { setZone: isEventFormat });
        const endDateLocaleString = luxonEndDate.setLocale(pageLocale).toLocaleString(DateTime.DATE_FULL);

        const isInvalidEndDate = luxonEndDate < luxonStartDate;
        // If end date is before start date, only show start date (end date invalid)
        if (isInvalidEndDate) return startDateLocaleString;

        const isSameDay = luxonStartDate.hasSame(luxonEndDate, 'day');
        const isSameMonth = luxonStartDate.hasSame(luxonEndDate, 'month');
        const isSameYear = luxonStartDate.hasSame(luxonEndDate, 'year');

        // If Start + End Dates are the same
        if (isSameDay) {
            return startDateLocaleString;
        }

        // If Start + End Dates are in different years (or YMD locales)
        if (!isSameYear) {
            return `${startDateLocaleString} – ${endDateLocaleString}`;
        }

        // If Start + End Dates are in the same month (and year)
        if (isSameMonth) {
            let dayDateRange = '';
            if (/(ja)/.test(pageLocale)) {
                //format date range for JA locale (e.g. 2021年9月29日-30日)
                dayDateRange = endDateLocaleString.replace(/^\d{4}.+?\d{1,2}.+?(\d{1,2}.+?)$/, '$1');
                return `${startDateLocaleString}-${dayDateRange}`;
            }
            if (/(ko|zh)/.test(pageLocale)) {
                //format date range for KO/ZH locales (e.g. 2021년 9월 29-30일)
                dayDateRange = `$1-${endDateLocaleString.replace(/^\d{4}.+?\d{1,2}.+?(\d{1,2}).+?$/, '$1')}$2`;
                return startDateLocaleString.replace(/^(\d{4}.+?\d{1,2}.+?\d{1,2})(.+?)$/, dayDateRange);
            }
            //format date range for all other locales
            dayDateRange = `$1-${endDateLocaleString.replace(/.*(\b\d{1,2}\b).*/, '$1')}`;
            return startDateLocaleString.replace(/(\b\d{1,2}\b)/, dayDateRange);
        }

        // If Start + End Dates are in different months in the same year
        if (isSameYear && !isSameMonth) {
            let monthDateRange = '';
            if (/(ja|ko|zh)/.test(pageLocale)) {
                //format date range for JA/KO/ZH locales (e.g. 2021年10月30日 – 11月1日)
                monthDateRange = endDateLocaleString.replace(/^\d{4}.+?(\d{1,2}.+?\d{1,2}.+?)$/, '$1');
                return `${startDateLocaleString} – ${monthDateRange}`;
            }
            //format date range for all other locales
            monthDateRange = `$1 – ${endDateLocaleString.replace(/(.+?),? \d{4}.*/, '$1')}$2`;
            return startDateLocaleString.replace(/(.+?)(,? \d{4}.*)/, monthDateRange);
        }

        return startDateLocaleString;
    };

    // ==========
    // RESULTS DISPLAY
    // ==========
    /**
     * @param {ResultItem} result
     * @returns {HTMLElement}
     */
    createResultElement = (result) => {
        let locale = (document.documentElement.lang || 'en-US')
            .replace('-__', '')
            .replace('_', '-');
        if (locale.toLowerCase() === 'en-mde' || locale.toLowerCase() === 'en-mde-mde') {
            locale = 'en-GB';
        }

        let sustTag = false;
        if (result.company != null && result.company.some((tag) => /company\/integrity-&-sustainability/.test(tag))) {
            sustTag = true;
        }
        const buildBadges = () => {
            if (!sustTag) {
                return '';
            }
            return `
                <div class="badge">
                    <div class="badge__icon-list">
                        <div class="badge__item">
                            <span class="label -green">
                                <span class="badge__icon filled-green raw-file-contents">
                                    <img src="/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/sustainability.svg" alt="${'Leaf icon'}" />
                                </span>
                                <span class="badge__text">Sustainability</span>
                            </span>
                        </div>
                    </div>
                </div>
            `;
        };

        const mainValues = {
            imageSrc: result.image,
            imageAlt: result.imageAlt,
            date: result.contentType?.includes('event-') || result.contentType?.includes('webinars')
                ? this.formatDate(true, result.eventStartDate, result.eventEndDate, locale)
                : this.formatDate(false, result.pubDate, null, locale),
            title: result.title,
            badges: buildBadges(),
            link: result.link,
            description: result.description,
            webinarType: 'non-webinar'
        };

        const detailValues = {};

        switch (result.contentType) {
        case 'event-in-person': {
            detailValues.location = result.eventLocation;
            detailValues.boothNumber = result.boothNumber;
            break;
        }
        case 'event-training': {
            detailValues.courseNumber = result.courseNumber;
            detailValues.courseTechnology = result.courseTechnology;
            detailValues.courseCategory = result.courseCategory;
            detailValues.courseType = result.courseType;
            break;
        }
        case 'event-webinar':
        case 'event-virtual':
        case 'webinars': {
            if (result.isOnDemand === 'true') {
                detailValues.duration = result.webinarLength;
                detailValues.date = ''; // Remove display date from on-demand
            } else {
                const { eventStartDate, eventEndDate } = result;
                if (eventStartDate && eventEndDate) {
                    const {
                        startTime, timeZoneLong, timeZoneShort
                    } = formatIsoDateTime(result.eventStartDate, result.eventEndDate);

                    detailValues.time = startTime;
                    detailValues.timeZoneShort = timeZoneShort;
                    detailValues.timeZoneLong = timeZoneLong;
                    detailValues.hasFutureDate = !hasDatePassed(result.eventStartDate);
                }
                detailValues.pdhLength = result.pdhLength?.replace(/.0$/, '');
            }
            detailValues.webinarLanguage = result.webinarLanguage;
            detailValues.webinarType = result.webinarType;

            if (result.webinarType === 'series') {
                const nextWebinar = this.getNextWebinar(result.webinarSeries);

                const {
                    startDate, endDate, startTime, timeZoneLong, timeZoneShort
                } = formatIsoDateTime(nextWebinar.startDateTime, nextWebinar.endDateTime);

                detailValues.date = `${startDate} ${endDate && endDate !== startDate ? ` - ${endDate}` : ''}`;
                detailValues.time = startTime;
                detailValues.timeZoneShort = timeZoneShort;
                detailValues.timeZoneLong = timeZoneLong;
                detailValues.duration = '';
            }
            break;
        }
        default:
            // do nothing
        }

        return createFilledElement(this.elements.resultTemplate, 'data-xt', {
            ...mainValues,
            ...detailValues,
            hasDetails: Object.values(detailValues).some((value) => value != null && value !== '')
        });
    };

    getNextWebinar(webinarSeries) {
        const now = new Date();

        const processedWebinars = webinarSeries.map((webinar) => ({
            ...webinar,
            startDateTime: webinar.startDateTime
        })).sort((a, b) => a.startDateTime - b.startDateTime);

        const nextWebinar = processedWebinars.find((webinar) => webinar.startDateTime > now.toISOString());

        return nextWebinar || processedWebinars[processedWebinars.length - 1];
    }

    // ==========
    // UI LOGIC
    // ==========
    /** @param {SubmitEvent?} ev */
    handleFilterForm = (ev = null) => {
        ev?.preventDefault();
        this.state.currentPage = 1;
        this.runQuery();
    };

    handleMatchMobileChange = () => {
        if (this.state.matchMobile.matches) {
            this.elements.filterDetails.forEach((detailsEl) => {
                detailsEl.open = false;
            });
        } else {
            this.elements.filterDialog.close();
        }
    };

    showMobileFilter = () => FS.showMobileFilter(this.elements.filterDialog);
    closeMobileFilter = () => FS.closeMobileFilter(this.elements.filterDialog);

    /** @param {Event} ev */
    handleDesktopSortSelect = (ev) => {
        FS.fillInput([...this.elements.filterForm.elements], this.constants.sortProp, ev.currentTarget.value, true);
        this.handleFilterForm();
    };

    handlePagination = pagination.handlePagination({
        handlePrevButton: () => this.state.currentPage -= 1,
        handleNextButton: () => this.state.currentPage += 1,
        handleInput: (input) => this.state.currentPage = input,
        then: this.runQuery,
    });

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        FS.addFacetListeners(this);
        FS.addPaginationListeners(this.elements.paginationForm);

        this.elements.filterMobileShow?.addEventListener('click', this.showMobileFilter);
        this.elements.filterMobileShowPlex?.addEventListener('click', this.showMobileFilter);
        this.elements.filterMobileClose.addEventListener('click', this.closeMobileFilter);
        this.elements.filterMobileSubmit?.addEventListener('click', this.closeMobileFilter);
        this.elements.filterMobileSubmitPlex?.addEventListener('click', this.closeMobileFilter);

        this.elements.filterForm.addEventListener('submit', this.handleFilterForm);
        this.elements.filterForm.addEventListener('reset', () => {
            FS.clearFacetSearches(this);
            window.setTimeout(this.handleFilterForm);
        });
        FS.onFormControlChange(this.elements.filterForm, this.handleFilterForm);

        this.elements.facetSearches.forEach((el) => el.addEventListener('input', FS.handleFacetSearch));
        FS.addViewToggleListeners(this);
        this.elements.desktopSortSelect?.addEventListener('change', this.handleDesktopSortSelect);

        // Pagination
        this.elements.paginationForm.addEventListener('submit', this.handlePagination);

        this.state.matchMobile.addEventListener('change', this.handleMatchMobileChange);
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    connectedCallback() {
        this.readUrlParams();
        this.addEventListeners();
        this.handleMatchMobileChange();
        this.runQuery(true);
        window.genericFilterInstance = this; // for debug purposes
    }
}

window.customElements.define(componentName, GenericFilter);
